// Conversion tracking utilities for CTA optimization
import { logUserAction } from './logger';

export interface ConversionEvent {
  event: string;
  category: 'cta' | 'form' | 'navigation' | 'engagement';
  label?: string;
  value?: number;
  properties?: Record<string, any>;
}

export interface ABTestVariant {
  id: string;
  name: string;
  weight: number; // 0-1, should sum to 1 across all variants
}

export interface ABTest {
  id: string;
  name: string;
  variants: ABTestVariant[];
  active: boolean;
}

// Simple A/B testing framework
class ConversionTracker {
  private tests: Map<string, ABTest> = new Map();
  private userVariants: Map<string, string> = new Map();

  // Track conversion events
  trackEvent(event: ConversionEvent): void {
    // Log to existing logger
    logUserAction(event.event, {
      category: event.category,
      label: event.label,
      value: event.value,
      ...event.properties,
      timestamp: new Date().toISOString(),
    });

    // Send to analytics (if available)
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', event.event, {
        event_category: event.category,
        event_label: event.label,
        value: event.value,
        custom_parameters: event.properties,
      });
    }

    // Send to other analytics services
    this.sendToAnalytics(event);
  }

  // Track CTA clicks with enhanced data
  trackCTAClick(
    ctaText: string, 
    ctaType: 'primary' | 'secondary' | 'tertiary',
    position: string,
    additionalData?: Record<string, any>
  ): void {
    this.trackEvent({
      event: 'cta_click',
      category: 'cta',
      label: `${ctaText} (${ctaType})`,
      properties: {
        cta_text: ctaText,
        cta_type: ctaType,
        position,
        ...additionalData,
      },
    });
  }

  // Track form interactions
  trackFormInteraction(
    formName: string,
    action: 'focus' | 'submit' | 'abandon',
    fieldName?: string,
    additionalData?: Record<string, any>
  ): void {
    this.trackEvent({
      event: 'form_interaction',
      category: 'form',
      label: `${formName}_${action}`,
      properties: {
        form_name: formName,
        action,
        field_name: fieldName,
        ...additionalData,
      },
    });
  }

  // A/B Testing functionality
  registerTest(test: ABTest): void {
    this.tests.set(test.id, test);
  }

  getVariant(testId: string, userId?: string): string | null {
    const test = this.tests.get(testId);
    if (!test || !test.active) return null;

    const userKey = userId || this.getSessionId();
    
    // Check if user already has a variant assigned
    const existingVariant = this.userVariants.get(`${testId}_${userKey}`);
    if (existingVariant) return existingVariant;

    // Assign variant based on weighted random selection
    const variant = this.selectWeightedVariant(test.variants);
    this.userVariants.set(`${testId}_${userKey}`, variant.id);
    
    // Track variant assignment
    this.trackEvent({
      event: 'ab_test_assignment',
      category: 'engagement',
      label: `${testId}_${variant.id}`,
      properties: {
        test_id: testId,
        variant_id: variant.id,
        variant_name: variant.name,
        user_key: userKey,
      },
    });

    return variant.id;
  }

  // Helper methods
  private selectWeightedVariant(variants: ABTestVariant[]): ABTestVariant {
    const random = Math.random();
    let accumulator = 0;
    
    for (const variant of variants) {
      accumulator += variant.weight;
      if (random <= accumulator) {
        return variant;
      }
    }
    
    // Fallback to first variant
    return variants[0];
  }

  private getSessionId(): string {
    if (typeof window === 'undefined') return 'server';
    
    let sessionId = sessionStorage.getItem('conversion_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('conversion_session_id', sessionId);
    }
    return sessionId;
  }

  private sendToAnalytics(event: ConversionEvent): void {
    // Send to custom analytics endpoint
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
      fetch('/api/analytics/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...event,
          session_id: this.getSessionId(),
          timestamp: new Date().toISOString(),
          url: window.location.href,
          user_agent: navigator.userAgent,
        }),
      }).catch(error => {
        console.warn('Failed to send analytics event:', error);
      });
    }
  }

  // Conversion funnel tracking
  trackFunnelStep(
    funnelName: string,
    step: string,
    stepNumber: number,
    additionalData?: Record<string, any>
  ): void {
    this.trackEvent({
      event: 'funnel_step',
      category: 'engagement',
      label: `${funnelName}_step_${stepNumber}`,
      value: stepNumber,
      properties: {
        funnel_name: funnelName,
        step_name: step,
        step_number: stepNumber,
        ...additionalData,
      },
    });
  }

  // Page engagement tracking
  trackEngagement(
    action: 'scroll_depth' | 'time_on_page' | 'click_depth',
    value: number,
    additionalData?: Record<string, any>
  ): void {
    this.trackEvent({
      event: 'page_engagement',
      category: 'engagement',
      label: action,
      value,
      properties: {
        action,
        ...additionalData,
      },
    });
  }
}

// Singleton instance
export const conversionTracker = new ConversionTracker();

// Convenience functions for common use cases
export const trackCTAClick = (
  ctaText: string,
  ctaType: 'primary' | 'secondary' | 'tertiary' = 'primary',
  position: string = 'hero',
  additionalData?: Record<string, any>
) => conversionTracker.trackCTAClick(ctaText, ctaType, position, additionalData);

export const trackFormSubmit = (
  formName: string,
  success: boolean = true,
  additionalData?: Record<string, any>
) => conversionTracker.trackFormInteraction(
  formName,
  success ? 'submit' : 'abandon',
  undefined,
  { success, ...additionalData }
);

export const trackEmailCapture = (
  email: string,
  source: string = 'hero',
  additionalData?: Record<string, any>
) => conversionTracker.trackEvent({
  event: 'email_capture',
  category: 'form',
  label: source,
  properties: {
    email_domain: email.split('@')[1],
    source,
    ...additionalData,
  },
});

// A/B Test configurations for CTA optimization
export const CTA_TESTS = {
  HERO_PRIMARY_BUTTON: {
    id: 'hero_primary_cta',
    name: 'Hero Primary CTA Copy Test',
    variants: [
      { id: 'control', name: 'Start Predicting Sales', weight: 0.5 },
      { id: 'variant_a', name: 'Get Started Free', weight: 0.25 },
      { id: 'variant_b', name: 'Try LotteryFlow Now', weight: 0.25 },
    ],
    active: true,
  },
  HERO_SECONDARY_BUTTON: {
    id: 'hero_secondary_cta',
    name: 'Hero Secondary CTA Copy Test',
    variants: [
      { id: 'control', name: 'See Live Demo', weight: 0.5 },
      { id: 'variant_a', name: 'Watch Demo', weight: 0.25 },
      { id: 'variant_b', name: 'View Demo', weight: 0.25 },
    ],
    active: true,
  },
} as const;

// Initialize A/B tests
Object.values(CTA_TESTS).forEach(test => {
  conversionTracker.registerTest(test);
});